/**
 * Payment Store
 * 
 * A singleton store for managing payment state across the app.
 * Uses pub/sub pattern for minimal re-renders - only components that
 * subscribe to the store will re-render when state changes.
 * 
 * This allows any modal/page to trigger payments without coupling
 * to GetCoinsModal or any specific component.
 */

export type PaymentItem = {
  id: string;
  title: string;
  price: number;
  originalPrice?: number;
  /** GC amount included in purchase */
  gcAmount?: number;
  /** SC amount included in purchase */
  scAmount?: number;
  /** Bonus GC amount */
  gcBonusAmount?: number;
  /** Bonus SC amount */
  scBonusAmount?: number;
  promoCode?: string;
  promoRewardType?: string;
  promoRewardValue?: number;
  promoBonusGC?: number;
  promoBonusSC?: number;
  /** If true, this is a scheduled offer (uses scheduled_offer table, not coin_package) */
  isScheduledOffer?: boolean;
  /** If true, use Coinflow Hosted Checkout only (no payment method selector) - auto-enabled when only coinflow_hosted method is active */
  coinflowOnly?: boolean;
  /** Checkout type for hosted checkout: 'card' (default) shows card/Apple Pay/Google Pay, 'crypto' shows only crypto */
  checkoutType?: 'card' | 'crypto';
};

export type PaymentCallbacks = {
  /** Called after successful payment */
  onSuccess?: () => void;
  /** Called if payment fails */
  onError?: (error: Error) => void;
  /** Called if user cancels/closes the payment modal */
  onCancel?: () => void;
  /** Called when user clicks back button - should reopen source (Get Coins, welcome offer, etc.) */
  onBack?: () => void;
  /** Called when switching to Coinflow-only mode - should close source drawer/modal */
  onSwitchToCoinflowOnly?: () => void;
};

type PaymentState = {
  isOpen: boolean;
  item: PaymentItem | null;
  callbacks: PaymentCallbacks;
  isProcessing: boolean;
  /** When true, show success animation before closing */
  showSuccess: boolean;
};

type Listener = () => void;

/**
 * Singleton payment store that lives outside React.
 * 
 * Benefits:
 * - No context provider overhead when not using payment
 * - Components only re-render when they explicitly subscribe
 * - Callbacks stored by reference, never cause re-renders
 * - Can be called from anywhere, even non-React code
 */
class PaymentStore {
  private state: PaymentState = {
    isOpen: false,
    item: null,
    callbacks: {},
    isProcessing: false,
    showSuccess: false,
  };

  private listeners = new Set<Listener>();

  /**
   * Get current state (snapshot)
   */
  getState = (): PaymentState => this.state;

  /**
   * Subscribe to state changes
   * Returns unsubscribe function
   */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * Notify all subscribers of state change
   */
  private notify = (): void => {
    this.listeners.forEach((listener) => listener());
  };

  /**
   * Open the payment modal with an item and optional callbacks
   */
  openPayment = (item: PaymentItem, callbacks: PaymentCallbacks = {}): void => {
    this.state = {
      ...this.state,
      isOpen: true,
      item,
      callbacks,
      isProcessing: false,
      showSuccess: false,
    };
    this.notify();
  };

  /**
   * Close the payment modal (user cancelled)
   */
  closePayment = (): void => {
    const { callbacks } = this.state;
    this.state = {
      isOpen: false,
      item: null,
      callbacks: {},
      isProcessing: false,
      showSuccess: false,
    };
    this.notify();
    // Call cancel callback after state update
    callbacks.onCancel?.();
  };

  /**
   * Go back to the source that opened payment (Get Coins, welcome offer, etc.)
   * Closes payment modal and calls onBack callback
   */
  goBack = (): void => {
    const { callbacks } = this.state;
    this.state = {
      isOpen: false,
      item: null,
      callbacks: {},
      isProcessing: false,
      showSuccess: false,
    };
    this.notify();
    // Call back callback after state update
    callbacks.onBack?.();
  };

  /**
   * Set processing state (for UI feedback)
   */
  setProcessing = (isProcessing: boolean): void => {
    this.state = { ...this.state, isProcessing };
    this.notify();
  };

  /**
   * Handle successful payment
   * Shows success animation then closes modal and calls success callback
   */
  handleSuccess = (): void => {
    const { callbacks } = this.state;
    
    // Close modal immediately and show success animation
    this.state = {
      ...this.state,
      isOpen: false,
      isProcessing: false,
      showSuccess: true,
    };
    this.notify();
    
    // After delay, close modal and call callback
    setTimeout(() => {
      this.state = {
        isOpen: false,
        item: null,
        callbacks: {},
        isProcessing: false,
        showSuccess: false,
      };
      this.notify();
      // Call success callback after state update
      callbacks.onSuccess?.();
    }, 2000); // 2 second delay for success animation
  };

  /**
   * Handle payment error
   * Keeps modal open but resets processing state
   */
  handleError = (error: Error): void => {
    const { callbacks } = this.state;
    this.state = { ...this.state, isProcessing: false };
    this.notify();
    // Call error callback
    callbacks.onError?.(error);
  };

  /**
   * Switch to Coinflow Hosted Checkout only mode
   * Used when user selects Coinflow Hosted from the payment method selector
   * @param checkoutType - 'card' (default) shows card/Apple Pay/Google Pay, 'crypto' shows only crypto
   */
  switchToCoinflowOnly = (checkoutType: 'card' | 'crypto' = 'card'): void => {
    if (!this.state.item) return;
    const { callbacks } = this.state;
    this.state = {
      ...this.state,
      item: {
        ...this.state.item,
        coinflowOnly: true,
        checkoutType,
      },
    };
    this.notify();
    // Call callback to close source drawer/modal (e.g., Get Coins)
    callbacks.onSwitchToCoinflowOnly?.();
  };

}

// Export singleton instance
export const paymentStore = new PaymentStore();
